import { useNavigate } from "react-router";
import { useMemo } from "react";
import { useCallback } from "react";

import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import { Blueprint, BlueprintState } from "types/generated";
import BlueprintShareDropdownMenuItem from "views/Account/Blueprint/components/ShareButton/DropdownMenuItem";
import useCanManageBlueprint from "shared/Blueprint/access/useCanManageBlueprint";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import TableSimpleCell from "ds/components/TableSimple/Cell";

import { showCreateBlueprintDrawer } from "../../CreateBlueprintDrawer";
import { showCreateStackDrawer } from "../../CreateStackDrawer";
import { showDeleteConfirmation } from "../../DeleteConfirmation";
import BlueprintsFeatureGateTooltip from "../../FeatureGate/Tooltip";
import { BlueprintActions } from "../../FeatureGate/types";

const BlueprintsTableRowActionsCell = ({
  item: blueprint,
  ...cellProps
}: TableSimpleCellProps<Blueprint>) => {
  const navigate = useNavigate();

  const canManageBlueprint = useCanManageBlueprint()(blueprint);

  const isPublished = blueprint.state === BlueprintState.Published;
  const isDraft = blueprint.state === BlueprintState.Draft;

  const onEditDetails = useCallback(() => {
    showCreateBlueprintDrawer({ id: blueprint.id, blueprint });
  }, [blueprint]);

  const onCreateStack = useCallback(() => {
    showCreateStackDrawer({
      id: blueprint.id,
      item: blueprint,
      onEditMetadata: onEditDetails,
    });
  }, [blueprint, onEditDetails]);

  const onCloneBlueprint = useCallback(() => {
    showCreateBlueprintDrawer({ id: blueprint.id, blueprint, isCloneMode: true });
  }, [blueprint]);

  const onEditDraft = useCallback(() => {
    navigate(`/blueprint/${blueprint.id}`);
  }, [blueprint.id, navigate]);

  const menuItems = useMemo(() => {
    const items = [];

    if (isPublished) {
      items.push(
        <BlueprintsFeatureGateTooltip
          key="create-stack"
          action={BlueprintActions.CreateStack}
          blueprint={blueprint}
          on={(props) => (
            <DropdownMenuItem {...props} onAction={onCreateStack}>
              Create stack
            </DropdownMenuItem>
          )}
          tooltipPlacement="left"
        />
      );
    }

    if (isDraft) {
      items.push(
        <BlueprintsFeatureGateTooltip
          key="edit-draft"
          action={BlueprintActions.Edit}
          blueprint={blueprint}
          on={(props) => (
            <DropdownMenuItem {...props} onAction={onEditDraft}>
              Edit draft
            </DropdownMenuItem>
          )}
          tooltipPlacement="left"
        />
      );
    }

    items.push(
      <BlueprintsFeatureGateTooltip
        key="edit-details"
        action={BlueprintActions.Edit}
        blueprint={blueprint}
        on={(props) => (
          <DropdownMenuItem {...props} onAction={onEditDetails}>
            Edit details
          </DropdownMenuItem>
        )}
        tooltipPlacement="left"
      />
    );
    items.push(
      <BlueprintsFeatureGateTooltip
        key="clone"
        action={BlueprintActions.CreateBlueprint}
        on={(props) => (
          <DropdownMenuItem {...props} onAction={onCloneBlueprint}>
            Clone
          </DropdownMenuItem>
        )}
        tooltipPlacement="left"
      />
    );

    if (isPublished) {
      items.push(
        <BlueprintsFeatureGateTooltip
          key="share"
          action={BlueprintActions.CreateStack}
          on={(props) => <BlueprintShareDropdownMenuItem {...props} blueprintId={blueprint.id} />}
          tooltipPlacement="left"
        />
      );
    }

    if (canManageBlueprint.result || !!canManageBlueprint.message) {
      items.push(
        <DropdownMenuItem
          key="delete"
          onAction={() => showDeleteConfirmation({ name: blueprint.name, id: blueprint.id })}
          danger
          isDisabled={!canManageBlueprint.result}
          tooltip={canManageBlueprint.message}
          tooltipPlacement="left"
        >
          Delete
        </DropdownMenuItem>
      );
    }

    return items;
  }, [
    isPublished,
    isDraft,
    canManageBlueprint.result,
    canManageBlueprint.message,
    blueprint,
    onCreateStack,
    onEditDraft,
    onEditDetails,
    onCloneBlueprint,
  ]);

  return (
    <TableSimpleCell {...cellProps}>
      {menuItems.length > 0 && (
        <DropdownMenuEllipsis tooltip="Blueprint actions">{menuItems}</DropdownMenuEllipsis>
      )}
    </TableSimpleCell>
  );
};

export default BlueprintsTableRowActionsCell;
